import React from 'react';
import { Link } from 'react-router-dom';
import { createPageUrl } from '../utils';
import { useLanguage } from '../components/LanguageContext';
import { CHARACTERS, CATEGORIES } from '../components/data/characters';
import { motion } from 'framer-motion';
import ImageComparison from '../components/ImageComparison';
import { 
  Sparkles, 
  Camera, 
  Zap, 
  Download, 
  Star, 
  ArrowRight,
  Play,
  Shield,
  Clock,
  Wand2
} from 'lucide-react';
import { Button } from '../components/ui/button';

export default function Home() {
  const { t, language, isRTL } = useLanguage();
  
  const featuredCharacters = CHARACTERS.slice(0, 8);

  const features = [
    {
      icon: Wand2,
      title: language === 'he' ? 'טכנולוגיית AI מתקדמת' : 'Advanced AI Technology',
      description: language === 'he' 
        ? 'שומר על הפנים שלך בדיוק תוך שינוי הגוף והרקע'
        : 'Preserves your face exactly while transforming body and background'
    },
    {
      icon: Clock,
      title: language === 'he' ? 'מהיר ומיידי' : 'Fast & Instant',
      description: language === 'he'
        ? 'קבל את התמונה שלך תוך 15-30 שניות'
        : 'Get your transformed photo in 15-30 seconds'
    },
    {
      icon: Shield,
      title: language === 'he' ? 'פרטיות מוחלטת' : 'Complete Privacy',
      description: language === 'he'
        ? 'התמונות שלך מאובטחות ופרטיות'
        : 'Your photos are secure and private'
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden min-h-[90vh] flex items-center">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-gradient-to-b from-violet-950/50 via-slate-950 to-slate-950" />
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-violet-600/20 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-pink-600/20 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }} />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Text Content */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className={isRTL ? 'text-right' : 'text-left'}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-violet-500/10 border border-violet-500/20 mb-6">
                <Sparkles className="w-4 h-4 text-violet-400" />
                <span className="text-sm font-medium text-violet-400">{t('freeCredits')}</span>
              </div>
              
              <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
                <span className="text-white">{t('heroTitle').split(' ').slice(0, -1).join(' ')}</span>{' '}
                <span className="gradient-text">{t('heroTitle').split(' ').slice(-1)}</span>
              </h1>
              
              <p className="text-xl text-slate-400 mb-8 max-w-xl leading-relaxed">
                {t('heroSubtitle')}
              </p>
              
              <div className="flex flex-wrap gap-4">
                <Link to={createPageUrl('Photobooth')}>
                  <Button size="lg" className="bg-gradient-to-r from-violet-600 to-pink-600 hover:from-violet-700 hover:to-pink-700 text-white px-8 py-6 text-lg rounded-xl glow-purple">
                    <Camera className="w-5 h-5 me-2" />
                    {t('startNow')}
                  </Button>
                </Link>
                <Link to={createPageUrl('Gallery')}>
                  <Button size="lg" className="bg-slate-700 hover:bg-slate-600 text-white px-8 py-6 text-lg rounded-xl font-semibold">
                    <Play className="w-5 h-5 me-2" />
                    {t('viewGallery')}
                  </Button>
                </Link>
              </div>

              {/* Stats */}
              <div className="flex gap-8 mt-12">
                <div>
                  <div className="text-3xl font-bold text-white">80+</div>
                  <div className="text-sm text-slate-500">{language === 'he' ? 'דמויות' : 'Characters'}</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-white">10k+</div>
                  <div className="text-sm text-slate-500">{language === 'he' ? 'משתמשים' : 'Users'}</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-white">4.9</div>
                  <div className="flex items-center gap-1 text-sm text-slate-500">
                    <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                    {language === 'he' ? 'דירוג' : 'Rating'}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Image Grid */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="grid grid-cols-2 gap-4">
                {featuredCharacters.slice(0, 4).map((char, idx) => (
                  <motion.div
                    key={char.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 + idx * 0.1 }}
                    className={`relative rounded-2xl overflow-hidden ${idx % 2 === 1 ? 'mt-8' : ''}`}
                  >
                    <div className="aspect-[3/4] bg-slate-800">
                      <img
                        src={char.image_url}
                        alt={language === 'he' ? char.name_he : char.name_en}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                    <div className="absolute bottom-4 left-4 right-4">
                      <span className="text-sm font-medium text-white">
                        {language === 'he' ? char.name_he : char.name_en}
                      </span>
                    </div>
                  </motion.div>
                ))}
              </div>
              
              {/* Floating Badge */}
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 3, repeat: Infinity }}
                className="absolute -bottom-4 -right-4 glass-card rounded-2xl px-6 py-4"
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-violet-600 to-pink-600 flex items-center justify-center">
                    <Zap className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="text-lg font-bold text-white">15s</div>
                    <div className="text-xs text-slate-400">
                      {language === 'he' ? 'זמן יצירה' : 'Generation Time'}
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Before/After Comparison Section */}
      <section className="py-20 bg-slate-950 relative overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full bg-violet-900/10 blur-3xl pointer-events-none" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold text-white mb-4">
              {language === 'he' ? 'הפוך כל תמונה ליצירת מופת' : 'Turn Any Photo into a Masterpiece'}
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              {language === 'he' 
                ? 'גרור את הסרגל כדי לראות את הקסם בפעולה' 
                : 'Drag the slider to see the magic in action'}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 items-center">
            {/* Example 1 */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <div className="bg-slate-900/50 p-4 rounded-3xl border border-white/10">
                <ImageComparison
                  beforeImage="/demos/girls/yoga-instructor.jpg"
                  afterImage="/demos/girls/sorceress.jpg"
                  className="w-full"
                  overlayText={language === 'he' ? 'מכשפה' : 'Sorceress'}
                />
                <div className="text-center mt-4">
                  <h3 className="text-xl font-semibold text-white">
                    {language === 'he' ? 'מכשפה' : 'Sorceress'}
                  </h3>
                </div>
              </div>
            </motion.div>

            {/* Example 2 */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
            >
              <div className="bg-slate-900/50 p-4 rounded-3xl border border-white/10">
                <ImageComparison
                  beforeImage="https://www.stryx.com/cdn/shop/articles/man-looking-attractive.jpg?v=1666662774"
                  afterImage="/demos/astronaut.jpg"
                  className="w-full"
                  overlayText={language === 'he' ? 'אסטרונאוט' : 'Astronaut'}
                />
                <div className="text-center mt-4">
                  <h3 className="text-xl font-semibold text-white">
                    {language === 'he' ? 'אסטרונאוט בחלל' : 'Space Astronaut'}
                  </h3>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What You Get Section */}
      <section className="py-20 relative bg-gradient-to-b from-slate-950 via-violet-950/10 to-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold text-white mb-4">
              {language === 'he' ? 'מה זה AI PhotoBooth?' : 'What is AI PhotoBooth?'}
            </h2>
            <p className="text-lg text-slate-400 max-w-3xl mx-auto">
              {language === 'he' 
                ? 'טכנולוגיית AI מתקדמת שהופכת את התמונה שלך לכל דמות שתרצה - מגיבורי-על, דרך מקצועות ועד דמויות היסטוריות. הפנים שלך נשמרות בדיוק, רק הגוף והרקע משתנים!'
                : 'Advanced AI technology that transforms your photo into any character you want - from superheroes, through professions to historical figures. Your face is preserved exactly, only the body and background change!'}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-6 mb-12">
            {[
              { 
                icon: Camera, 
                title: language === 'he' ? 'צלם או העלה' : 'Snap or Upload',
                desc: language === 'he' ? 'צלם סלפי או העלה תמונה קיימת' : 'Take a selfie or upload an existing photo'
              },
              { 
                icon: Wand2, 
                title: language === 'he' ? 'בחר דמות' : 'Choose Character',
                desc: language === 'he' ? 'בחר מתוך 80+ דמויות מגוונות' : 'Pick from 80+ diverse characters'
              },
              { 
                icon: Zap, 
                title: language === 'he' ? 'AI קסום' : 'AI Magic',
                desc: language === 'he' ? 'הAI יוצר את התמונה תוך 15-30 שניות' : 'AI generates your image in 15-30 seconds'
              },
              { 
                icon: Download, 
                title: language === 'he' ? 'הורד ושתף' : 'Download & Share',
                desc: language === 'he' ? 'קבל תמונה באיכות גבוהה לשיתוף' : 'Get a high-quality image to share'
              },
            ].map((step, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="glass-card rounded-2xl p-6 text-center"
              >
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-violet-600 to-pink-600 flex items-center justify-center mx-auto mb-4">
                  <step.icon className="w-6 h-6 text-white" />
                </div>
                <div className="text-2xl font-bold text-violet-400 mb-2">{idx + 1}</div>
                <h3 className="text-lg font-semibold text-white mb-2">{step.title}</h3>
                <p className="text-slate-400 text-sm">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold text-white mb-4">
              {language === 'he' ? 'למה AI PhotoBooth?' : 'Why AI PhotoBooth?'}
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="glass-card rounded-2xl p-8 hover:bg-white/5 transition-colors"
              >
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-violet-600/20 to-pink-600/20 flex items-center justify-center mb-6">
                  <feature.icon className="w-7 h-7 text-violet-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">{feature.title}</h3>
                <p className="text-slate-400 leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Characters Preview */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-violet-950/20 to-slate-950" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-white mb-4">
              {language === 'he' ? 'גלה 80+ דמויות' : 'Discover 80+ Characters'}
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              {language === 'he'
                ? 'מגיבורי-על ועד מקצועות, מפנטזיה ועד היסטוריה'
                : 'From superheroes to professionals, from fantasy to history'}
            </p>
          </motion.div>

          {/* Category Pills */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {CATEGORIES.slice(1, 7).map((cat) => (
              <Link
                key={cat.id}
                to={createPageUrl('Gallery')}
                className="px-4 py-2 rounded-full glass-card text-sm font-medium text-slate-300 hover:bg-violet-600/20 hover:text-violet-400 transition-colors cursor-pointer"
              >
                {language === 'he' ? cat.name_he : cat.name_en}
              </Link>
            ))}
            <Link 
              to={createPageUrl('Gallery')}
              className="px-4 py-2 rounded-full bg-violet-600/20 text-sm font-medium text-violet-400 hover:bg-violet-600/30 transition-colors cursor-pointer"
            >
              +{CATEGORIES.length - 7} {language === 'he' ? 'עוד' : 'more'}
            </Link>
          </div>

          {/* Character Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {CHARACTERS.slice(0, 12).map((char, idx) => (
              <Link
                key={char.id}
                to={`${createPageUrl('Photobooth')}?character=${char.id}`}
              >
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.05 }}
                  whileHover={{ scale: 1.05 }}
                  className="group relative rounded-xl overflow-hidden cursor-pointer"
                >
                  <div className="aspect-square bg-slate-800">
                    <img
                      src={char.image_url}
                      alt={language === 'he' ? char.name_he : char.name_en}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="absolute bottom-0 left-0 right-0 p-3 translate-y-full group-hover:translate-y-0 transition-transform">
                    <span className="text-sm font-medium text-white">
                      {language === 'he' ? char.name_he : char.name_en}
                    </span>
                  </div>
                </motion.div>
              </Link>
            ))}
          </div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Link to={createPageUrl('Gallery')}>
              <Button size="lg" className="bg-slate-700 hover:bg-slate-600 text-white font-semibold">
                {language === 'he' ? 'צפה בכל הדמויות' : 'View All Characters'}
                <ArrowRight className={`w-5 h-5 ${isRTL ? 'mr-2 rotate-180' : 'ml-2'}`} />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card rounded-3xl p-12 text-center relative overflow-hidden"
          >
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-64 bg-violet-600/30 rounded-full blur-3xl" />
            
            <div className="relative">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-violet-500/10 border border-violet-500/20 mb-6">
                <Sparkles className="w-4 h-4 text-violet-400" />
                <span className="text-sm font-medium text-violet-400">{t('freeCredits')}</span>
              </div>
              
              <h2 className="text-4xl font-bold text-white mb-4">
                {language === 'he' ? 'מוכנים להתחיל?' : 'Ready to Transform?'}
              </h2>
              
              <p className="text-lg text-slate-400 mb-8 max-w-xl mx-auto">
                {language === 'he'
                  ? 'הצטרפו עכשיו וקבלו 5 קרדיטים חינם ליצירת התמונות הראשונות שלכם'
                  : 'Join now and get 5 free credits to create your first transformations'}
              </p>
              
              <Link to={createPageUrl('Photobooth')}>
                <Button size="lg" className="bg-gradient-to-r from-violet-600 to-pink-600 hover:from-violet-700 hover:to-pink-700 text-white px-12 py-6 text-lg rounded-xl glow-purple">
                  <Camera className="w-5 h-5 me-2" />
                  {t('startNow')}
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
